/**
 * FDRPortal — standalone, no Core SSO page where an invited FDR uploads
 * their CMS universe file.
 *
 * Routed by App.js when window.location.pathname matches /fdr/portal/:token.
 *
 * Flow:
 *   1. Mount → POST /api/fdr/portal/verify with the path token.
 *      On success, show the invitation context (plan, protocol, universe,
 *      period, MFA channel) and the "Send verification code" CTA.
 *   2. POST /api/fdr/portal/mfa/challenge → server sends an OTP via SMS
 *      (Twilio) or email (SendGrid). UI shows the masked recipient and
 *      a 6-digit input.
 *   3. POST /api/fdr/portal/mfa/verify → server returns an MFA pass token
 *      held only in component state. Drag/drop upload becomes enabled.
 *   4. Multipart POST /api/fdr/portal/submit with Bearer token + X-MFA-Pass.
 *      Server runs validation server-side and returns a summary, which the
 *      UI renders so the FDR has confirmation of receipt.
 *
 * Security notes:
 *   - The token in the URL path is never logged client-side and is sent
 *     only over HTTPS (the LB enforces it).
 *   - The MFA pass lives in component state and is cleared on unmount.
 *   - All API calls go to same-origin so no CORS preflight surfaces the
 *     token in OPTIONS requests.
 */

import React, { useEffect, useState } from 'react';
import { SCRUBBER_API_BASE } from '../../api/config';
import FDRUniverseReview from './FDRUniverseReview';

const STEP = {
  VERIFYING: 'verifying',
  NEEDS_MFA: 'needs_mfa',
  AWAITING_OTP: 'awaiting_otp',
  READY_TO_UPLOAD: 'ready_to_upload',
  UPLOADING: 'uploading',
  REVIEWING: 'reviewing',      // NEW — FDR sees their annotated universe
  FINALIZED: 'finalized',      // NEW — FDR has clicked "Submit final to plan"
  ERROR: 'error',
};

function maskTokenForDisplay(token) {
  if (!token || token.length < 8) return '••••••••';
  return `${token.slice(0, 4)}…${token.slice(-4)}`;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toISOString().slice(0, 10);
}

export default function FDRPortal({ token }) {
  const [step, setStep] = useState(STEP.VERIFYING);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);

  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);

  const [mfaPass, setMfaPass] = useState(null);
  const [mfaPassExpires, setMfaPassExpires] = useState(null);

  const [file, setFile] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [reviewData, setReviewData] = useState(null);    // {universe, errors, categoryCounts}
  const [finalizedResult, setFinalizedResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // ── Step 1: verify token ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SCRUBBER_API_BASE}/api/fdr/portal/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok || !json.success) {
          setError(json.message || 'This invitation is not valid.');
          setStep(STEP.ERROR);
          return;
        }
        setInvitation(json.invitation);
        setStep(STEP.NEEDS_MFA);
      } catch (e) {
        if (cancelled) return;
        setError('Could not reach the submission portal. Please check your connection.');
        setStep(STEP.ERROR);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // ── Step 2: request OTP ────────────────────────────────────────────────
  async function requestOtp() {
    setOtpBusy(true);
    setError(null);
    try {
      const res = await fetch(`${SCRUBBER_API_BASE}/api/fdr/portal/mfa/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || 'Could not send a verification code.');
        return;
      }
      setChallenge(json.challenge);
      setStep(STEP.AWAITING_OTP);
    } catch (e) {
      setError('Could not send a verification code. Please try again.');
    } finally {
      setOtpBusy(false);
    }
  }

  // ── Step 3: verify OTP → mint MFA pass ─────────────────────────────────
  async function verifyOtp() {
    if (!/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit code.');
      return;
    }
    setOtpBusy(true);
    setError(null);
    try {
      const res = await fetch(`${SCRUBBER_API_BASE}/api/fdr/portal/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          token,
          challenge_id: challenge.challenge_id,
          code: otp,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || 'Code did not match.');
        return;
      }
      setMfaPass(json.mfa.pass_token);
      setMfaPassExpires(json.mfa.expires_at);
      setStep(STEP.READY_TO_UPLOAD);
    } catch (e) {
      setError('Verification failed. Please try again.');
    } finally {
      setOtpBusy(false);
    }
  }

  // ── Step 4: upload draft → server returns full annotated universe ──────
  // Each submit creates a new DRAFT (max 3 per invitation). The MFA pass is
  // preserved (not consumed) so the FDR can review and either resubmit a
  // corrected file or click "Submit final to plan" via STEP.REVIEWING.
  async function submit() {
    if (!file) { setError('Choose a file first.'); return; }
    if (!mfaPass) { setError('Your verification has expired. Request a new code.'); return; }
    setStep(STEP.UPLOADING);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('token', token);
      fd.append('mfa_pass', mfaPass);

      const res = await fetch(`${SCRUBBER_API_BASE}/api/fdr/portal/submit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-MFA-Pass': mfaPass,
        },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        // Server-side cap exceeded — give a clear instruction.
        if (json.code === 'MAX_ATTEMPTS_EXCEEDED') {
          setError(json.message || 'No more attempts available for this invitation.');
          setStep(STEP.READY_TO_UPLOAD);
          return;
        }
        setError(json.message || 'Submission failed.');
        setStep(STEP.READY_TO_UPLOAD);
        return;
      }
      setSubmitResult(json.submission);
      setReviewData({
        universe: json.universe || [],
        errors: json.errors || [],
        categoryCounts: json.categoryCounts || {},
      });
      setStep(STEP.REVIEWING);
    } catch (e) {
      setError('Submission failed. Please try again with a fresh code.');
      setStep(STEP.READY_TO_UPLOAD);
    }
  }

  // ── Step 5a: FDR clicked "Upload a corrected file" — back to picker ────
  function handleRequestResubmit() {
    setFile(null);
    setReviewData(null);
    setSubmitResult(null);
    setError(null);
    setStep(STEP.READY_TO_UPLOAD);
  }

  // ── Step 5b: FDR clicked "Submit final to plan" — server confirmed ─────
  function handleFinalized(finalizeResponse) {
    setFinalizedResult(finalizeResponse);
    // MFA pass is now consumed server-side; clear local copy too.
    setMfaPass(null);
    setStep(STEP.FINALIZED);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) setFile(f);
  }

  // When the FDR is reviewing their annotated table, the layout needs room
  // for the spreadsheet. Other steps stay narrow for a clean form feel.
  const wideStep = step === STEP.REVIEWING;
  const containerWidth = wideStep ? 'max-w-6xl' : 'max-w-2xl';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4 font-sans text-slate-900">
      <header className={`w-full ${containerWidth} mb-6`}>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Precision Audit Scrubber</div>
        <h1 className="text-2xl font-bold mt-1">FDR universe submission</h1>
        <p className="text-sm text-slate-500 mt-1">
          Secure portal · token <code className="bg-slate-200 px-1.5 py-0.5 rounded text-[11px]">{maskTokenForDisplay(token)}</code>
        </p>
      </header>

      <main className={`w-full ${containerWidth} bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden`}>
        {step === STEP.VERIFYING && (
          <div className="p-8 text-center text-slate-500">
            <div className="inline-block h-6 w-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin mb-3" />
            <div>Verifying your invitation…</div>
          </div>
        )}

        {step === STEP.ERROR && (
          <div className="p-8 text-center">
            <div className="text-rose-600 text-3xl mb-2">⚠</div>
            <div className="font-semibold mb-1">This link can’t be used</div>
            <div className="text-sm text-slate-500">{error}</div>
            <div className="text-xs text-slate-400 mt-4">If you believe this is a mistake, contact the plan compliance officer who sent you this link.</div>
          </div>
        )}

        {invitation && step !== STEP.VERIFYING && step !== STEP.ERROR && (
          <>
            <div className="p-6 border-b border-slate-200 bg-slate-50">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Submission requested</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="text-slate-500">FDR</div>
                <div className="font-semibold">{invitation.fdr_organization_name}</div>
                <div className="text-slate-500">Protocol</div>
                <div className="font-semibold">{invitation.protocol}</div>
                <div className="text-slate-500">Universe</div>
                <div className="font-semibold">{invitation.universe_type}</div>
                <div className="text-slate-500">Period</div>
                <div className="font-semibold">{formatDate(invitation.universe_period_start)} → {formatDate(invitation.universe_period_end)}</div>
                <div className="text-slate-500">Expires</div>
                <div className="font-semibold">{new Date(invitation.expires_at).toUTCString()}</div>
              </div>
              {invitation.message_to_fdr && (
                <div className="mt-4 text-sm bg-white border-l-2 border-rose-500 rounded px-3 py-2 text-slate-700 whitespace-pre-wrap">
                  {invitation.message_to_fdr}
                </div>
              )}
            </div>

            {step === STEP.NEEDS_MFA && (
              <div className="p-6">
                <h2 className="font-semibold text-lg mb-1">Step 1 of 2 — Verify your identity</h2>
                <p className="text-sm text-slate-500 mb-4">
                  We’ll send a 6-digit code by {invitation.mfa_channel === 'sms' ? 'SMS' : 'email'} to{' '}
                  <span className="font-mono">{invitation.mfa_channel === 'sms' ? invitation.fdr_phone_masked : invitation.fdr_email_masked}</span>.
                </p>
                <button
                  onClick={requestOtp}
                  disabled={otpBusy}
                  className="bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-lg text-sm"
                >
                  {otpBusy ? 'Sending…' : `Send code by ${invitation.mfa_channel === 'sms' ? 'SMS' : 'email'}`}
                </button>
                {error && <div className="mt-3 text-sm text-rose-600">{error}</div>}
              </div>
            )}

            {step === STEP.AWAITING_OTP && challenge && (
              <div className="p-6">
                <h2 className="font-semibold text-lg mb-1">Enter your verification code</h2>
                <p className="text-sm text-slate-500 mb-4">
                  We sent a 6-digit code to <span className="font-mono">{challenge.recipient_masked}</span>.
                  It expires at {new Date(challenge.expires_at).toLocaleTimeString()}.
                </p>
                <input
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="font-mono text-3xl tracking-[0.4em] text-center w-full max-w-xs border border-slate-300 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500"
                  placeholder="••••••"
                />
                {challenge.dev_otp && (
                  <div className="text-[10px] text-amber-600 mt-1">DEV ONLY — code: <code>{challenge.dev_otp}</code></div>
                )}
                <div className="mt-3 flex gap-3">
                  <button
                    onClick={verifyOtp}
                    disabled={otpBusy || otp.length !== 6}
                    className="bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-lg text-sm"
                  >
                    {otpBusy ? 'Verifying…' : 'Verify'}
                  </button>
                  <button
                    onClick={requestOtp}
                    disabled={otpBusy}
                    className="text-rose-600 hover:text-rose-700 underline text-sm self-center"
                  >
                    Resend code
                  </button>
                </div>
                {error && <div className="mt-3 text-sm text-rose-600">{error}</div>}
              </div>
            )}

            {(step === STEP.READY_TO_UPLOAD || step === STEP.UPLOADING) && (
              <div className="p-6">
                <h2 className="font-semibold text-lg mb-1">Step 2 of 2 — Upload your universe file</h2>
                <p className="text-sm text-slate-500 mb-4">
                  Accepted: .xlsx, .xls (universe file as exported from your source system).
                  Verification valid until {mfaPassExpires ? new Date(mfaPassExpires).toLocaleTimeString() : ''}.
                </p>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    dragOver ? 'border-rose-500 bg-rose-50' : 'border-slate-300 bg-slate-50'
                  }`}
                  onClick={() => document.getElementById('fdr-file-input')?.click()}
                >
                  <input
                    id="fdr-file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files && e.target.files[0])}
                  />
                  {file ? (
                    <div>
                      <div className="text-sm text-slate-700 font-semibold truncate max-w-md mx-auto">{file.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
                      <div className="text-xs text-rose-600 mt-2 underline">Click or drop to replace</div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-3xl mb-2">📥</div>
                      <div className="text-sm text-slate-700 font-semibold">Drop your file here</div>
                      <div className="text-xs text-slate-500 mt-1">or click to browse</div>
                    </div>
                  )}
                </div>
                <button
                  onClick={submit}
                  disabled={!file || step === STEP.UPLOADING}
                  className="mt-4 w-full bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold px-5 py-3 rounded-lg text-sm"
                >
                  {step === STEP.UPLOADING ? 'Uploading…' : 'Submit universe'}
                </button>
                {error && <div className="mt-3 text-sm text-rose-600">{error}</div>}
                <div className="mt-6 text-[11px] text-slate-400 leading-relaxed">
                  Your file is transmitted over TLS and stored encrypted at rest (AES-256-GCM) under
                  envelope encryption. Only the inviting plan’s authorized users can retrieve it. A
                  cryptographic hash and your submission metadata are recorded in an append-only audit log.
                </div>
              </div>
            )}

            {step === STEP.REVIEWING && submitResult && reviewData && (
              <FDRUniverseReview
                submission={submitResult}
                universe={reviewData.universe}
                errors={reviewData.errors}
                categoryCounts={reviewData.categoryCounts}
                invitationToken={token}
                mfaPass={mfaPass}
                onRequestResubmit={handleRequestResubmit}
                onFinalized={handleFinalized}
              />
            )}

            {step === STEP.FINALIZED && finalizedResult && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xl">✓</div>
                  <div>
                    <div className="font-semibold">Submission sent to plan</div>
                    <div className="text-xs text-slate-500">
                      {new Date(finalizedResult.finalized_at).toLocaleString()} · final submission recorded
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm bg-slate-50 rounded-lg p-4">
                  <div className="text-slate-500">Submission ID</div>
                  <div className="font-mono text-[11px] break-all">{finalizedResult.submission_id}</div>
                  <div className="text-slate-500">Attempt used</div>
                  <div>#{finalizedResult.attempt_number}</div>
                  {submitResult && (
                    <>
                      <div className="text-slate-500">File</div>
                      <div className="font-mono break-all">{submitResult.file_name}</div>
                      <div className="text-slate-500">SHA-256</div>
                      <div className="font-mono text-[10px] break-all">{submitResult.file_hash}</div>
                    </>
                  )}
                </div>
                <div className="mt-4 text-xs text-slate-500 leading-relaxed">
                  You may close this window. The inviting plan can now see your submission.
                  This invitation link is no longer usable for new uploads — if you need to
                  change anything, contact the plan compliance officer who sent you the link.
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
